import {
  isKeyboardFirmwareUpgradeInProgress,
  readUpgradeXingshanDeviceIdFromStorage,
} from "@eweadn-qiankun/shared-utils";

type HidDeviceLike = {
  id?: string;
  collections?: unknown[];
  vendorId?: number;
  productId?: number;
  productName?: string;
  opened?: boolean;
  open?: () => Promise<void>;
  close?: () => Promise<void>;
};

type HidServiceLike = {
  device: HidDeviceLike | null;
  id: string;
  reconnection: (
    device: HidDeviceLike | null,
    id: string,
    isUpgrading?: boolean
  ) => Promise<unknown>;
  devices?: (opts?: { isUpgrading?: boolean }) => Promise<HidDeviceLike[]>;
  __eweadnUpgradeReconnectPatched?: boolean;
  __eweadnUpgradeDevicesPatched?: boolean;
};

type DeviceBaseLike = {
  deviceBase?: HidServiceLike;
  hidService?: HidServiceLike;
  reconnection?: HidServiceLike["reconnection"];
  getDevices?: (opts?: { isUpgrading?: boolean }) => Promise<HidDeviceLike[]>;
  device?: HidDeviceLike | null;
  id?: string;
};

function resolveHidService(keyboard: DeviceBaseLike): HidServiceLike | null {
  // Keyboard API: deviceBase = Zs；Zs.hidService 才是真正读写 this.device 的 HID 层
  const zs = keyboard?.deviceBase ?? keyboard;
  return (zs?.hidService ?? zs) as HidServiceLike | null;
}

function resolveDeviceId(
  device: HidDeviceLike | null | undefined,
  id: string | undefined,
  hid: HidServiceLike
): string {
  return String(
    id || device?.id || readUpgradeXingshanDeviceIdFromStorage() || hid.id || ""
  ).trim();
}

/**
 * 修复 @sparklinkplayjoy/sdk-keyboard-v2 升级/重连竞态（概率性）：
 *
 * SDK hidService.reconnection 顺序有 bug：
 *   closeDevice() → 读 this.device.id → (isUpgrading 时才) this.device = e
 * closeDevice 后 this.device 可能为 null，或并发重连时 e/this.device 为空，
 * 表现为：Cannot read properties of null (reading 'id')
 *
 * 另：appToBoot 后 getDevices 偶发为空会立刻 requestDevice；
 * 无用户手势时被守卫拦成 []，表现为卡在 appToBoot end / noDevice。
 */
export function patchXingshanUpgradeReconnection(
  keyboard: DeviceBaseLike
): void {
  const hid = resolveHidService(keyboard);
  if (!hid) return;

  if (
    typeof hid.reconnection === "function" &&
    !hid.__eweadnUpgradeReconnectPatched
  ) {
    const native = hid.reconnection.bind(hid);
    hid.reconnection = async (device, id, isUpgrading) => {
      const upgrading =
        Boolean(isUpgrading) || isKeyboardFirmwareUpgradeInProgress();
      const target = device ?? hid.device;
      if (!target) {
        console.warn(
          "[xingshanUpgradeReconnectFix] reconnection skipped: device is null"
        );
        return false;
      }

      // native 在 closeDevice 后立刻读 this.device.id / e.id，必须先保证非空且带 id
      hid.device = target;
      const resolvedId = resolveDeviceId(target, id, hid);
      if (resolvedId) {
        target.id = resolvedId;
        hid.id = resolvedId;
      }

      try {
        return await native(target, resolvedId, upgrading);
      } catch (e) {
        console.error("[xingshanUpgradeReconnectFix] reconnection failed:", e);
        return false;
      }
    };
    hid.__eweadnUpgradeReconnectPatched = true;
  }

  if (typeof hid.devices === "function" && !hid.__eweadnUpgradeDevicesPatched) {
    const nativeDevices = hid.devices.bind(hid);
    hid.devices = async (opts) => {
      const upgrading =
        Boolean(opts?.isUpgrading) || isKeyboardFirmwareUpgradeInProgress();
      if (!upgrading) {
        return nativeDevices(opts);
      }

      // Boot 枚举有延迟：先轮询，避免过早 requestDevice 被守卫拦成空数组
      const delays = [0, 300, 600, 1000, 1500];
      for (const wait of delays) {
        if (wait > 0) {
          await new Promise((r) => setTimeout(r, wait));
        }
        try {
          const list = await nativeDevices(opts);
          if (Array.isArray(list) && list.length > 0) return list;
        } catch (e) {
          console.warn(
            "[xingshanUpgradeReconnectFix] devices() retry failed:",
            e
          );
        }
      }
      return nativeDevices(opts);
    };
    hid.__eweadnUpgradeDevicesPatched = true;
  }
}
